import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  AlertColor,
  CircularProgress,
  MenuItem,
  Snackbar,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  InputAdornment,
  Tooltip
} from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Folder as FolderIcon
} from '@mui/icons-material';
import { apiUrl } from '../utils/api';
import LoadingOverlay from '../components/LoadingOverlay';
import StyledDataGrid from '../components/StyledDataGrid';

interface Project {
  project_id: number;
  project_number: string;
  project_name: string;
  project_job_reference: string;
  currency: string;
  company_id: number;
}

interface Company {
  company_id: number;
  company_code: string;
  company_name: string;
}

interface ProjectsProps {
  projects: Project[];
  fetchProjects: () => Promise<void>;
  access?: string;
  projectId?: number | null;
  companyId?: number | null;
}

const Projects: React.FC<ProjectsProps> = ({ projects, fetchProjects, access = 'none', projectId = null, companyId = null }) => {
  const readOnly = access !== 'write';
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: AlertColor;
  }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    open: boolean;
    project: Project | null;
  }>({
    open: false,
    project: null
  });
  const [formData, setFormData] = useState({
    project_number: '',
    project_name: '',
    project_job_reference: '',
    currency: '',
    company_id: '' as string | number
  });

  const showNotification = (message: string, severity: AlertColor = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const fetchCompanies = () => {
    fetch(apiUrl('/api/companies'), {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => setCompanies(data.companies || []))
      .catch(() => setCompanies([]));
  };

  useEffect(() => {
    if (access === 'none') return;
    setLoading(true);
    Promise.all([
      fetchProjects(),
      fetchCompanies()
    ]).finally(() => setLoading(false));
  }, [access, fetchProjects]);

  const handleOpenDialog = (project: Project | null = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        project_number: project.project_number || '',
        project_name: project.project_name || '',
        project_job_reference: project.project_job_reference || '',
        currency: project.currency || '',
        company_id: project.company_id || ''
      });
    } else {
      setEditingProject(null);
      setFormData({
        project_number: '',
        project_name: '',
        project_job_reference: '',
        currency: '',
        company_id: companyId || ''  // Auto-populate with selected company
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProject(null);
    setError(null);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validation
      if (!formData.project_name.trim()) {
        throw new Error('Project name is required');
      }
      if (!formData.project_number.trim()) {
        throw new Error('Project number is required');
      }
      if (!formData.company_id) {
        throw new Error('Company selection is required');
      }

      const url = editingProject
        ? apiUrl(`/api/projects/${editingProject.project_id}`)
        : apiUrl('/api/projects');

      const response = await fetch(url, {
        method: editingProject ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        await fetchProjects();
        handleCloseDialog();
        showNotification(
          editingProject
            ? 'Project updated successfully!'
            : 'Project created successfully!',
          'success'
        );
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save project');
      }
    } catch (err: any) {
      setError(err.message);
      showNotification(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (project: Project) => {
    if (readOnly) return;
    setDeleteConfirmDialog({ open: true, project });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmDialog.project) return;

    const projectId = deleteConfirmDialog.project.project_id;
    setDeleteConfirmDialog({ open: false, project: null });

    try {
      setDeletingId(projectId);
      const response = await fetch(apiUrl(`/api/projects/${projectId}`), {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchProjects();
        showNotification('Project deleted successfully!', 'success');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete project');
      }
    } catch (err: any) {
      showNotification(err.message || 'Error deleting project', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const getCompanyName = (companyId: number) => {
    const company = companies.find(c => c.company_id === companyId);
    return company ? `${company.company_code} - ${company.company_name}` : 'No Company';
  };

  // Filter projects based on selected company and search term
  const filteredProjects = projects.filter(project => {
    // First filter by company if companyId is selected
    if (companyId && project.company_id !== companyId) {
      return false;
    }

    // Then filter by search term
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const companyName = getCompanyName(project.company_id).toLowerCase();
    return (
      project.project_number?.toLowerCase().includes(searchLower) ||
      project.project_name?.toLowerCase().includes(searchLower) ||
      project.project_job_reference?.toLowerCase().includes(searchLower) ||
      project.currency?.toLowerCase().includes(searchLower) ||
      companyName.includes(searchLower)
    );
  });

  // DataGrid columns definition
  const columns: GridColDef[] = [
    {
      field: 'project_id',
      headerName: 'ID',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={`#${params.value}`}
          size="small"
          variant="outlined"
          color="primary"
          onClick={(e) => e.stopPropagation()}
        />
      )
    },
    {
      field: 'project_number',
      headerName: 'Number',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" fontWeight={600}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'project_name',
      headerName: 'Name',
      flex: 2,
      minWidth: 200
    },
    {
      field: 'project_job_reference',
      headerName: 'Job Reference',
      flex: 1.5,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'currency',
      headerName: 'Currency',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => (
        params.value ? (
          <Chip label={params.value} size="small" color="primary" variant="outlined" onClick={(e) => e.stopPropagation()} />
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        )
      )
    },
    {
      field: 'company_id',
      headerName: 'Company',
      flex: 1.5,
      minWidth: 180,
      valueGetter: (value, row) => {
        // Return company name for filtering to work
        return getCompanyName(row.company_id);
      },
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={getCompanyName(params.row.company_id)}
          size="small"
          color={params.row.company_id ? 'success' : 'default'}
          variant="outlined"
          onClick={(e) => e.stopPropagation()}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      minWidth: 120,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {!readOnly && (
            <>
              <Tooltip title="Edit Project">
                <IconButton
                  size="small"
                  onClick={() => handleOpenDialog(params.row)}
                  disabled={!!deletingId}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Project">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(params.row)}
                  disabled={deletingId === params.row.project_id}
                  color="error"
                >
                  {deletingId === params.row.project_id ? (
                    <CircularProgress size={20} />
                  ) : (
                    <DeleteIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      )
    }
  ];

  if (access === 'none') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          You don't have permission to manage projects.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      <LoadingOverlay loading={loading} size={50} />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3, flexShrink: 0 }}>
        {!readOnly && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Project
          </Button>
        )}
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <TextField
          fullWidth
          placeholder="Search projects by number, name, job reference, currency, or company..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          size="small"
        />
      </Box>

      {/* Projects DataGrid */}
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <StyledDataGrid
          rows={filteredProjects}
          columns={columns}
          getRowId={(row) => row.project_id}
          loading={loading}
          paginationMode="client"
        />
      </Box>

      {/* Project Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingProject ? 'Edit Project' : 'Add New Project'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <TextField
              label="Project Number"
              fullWidth
              required
              value={formData.project_number}
              onChange={(e) => handleInputChange('project_number', e.target.value)}
              placeholder="e.g., PRJ-001"
            />

            <TextField
              label="Project Name"
              fullWidth
              required
              value={formData.project_name}
              onChange={(e) => handleInputChange('project_name', e.target.value)}
              placeholder="Enter project name"
            />

            <TextField
              label="Job Reference"
              fullWidth
              value={formData.project_job_reference}
              onChange={(e) => handleInputChange('project_job_reference', e.target.value)}
              placeholder="Optional job reference"
            />

            <TextField
              label="Currency"
              fullWidth
              value={formData.currency}
              onChange={(e) => handleInputChange('currency', e.target.value)}
              placeholder="e.g., USD, EUR, GBP"
            />

            <FormControl fullWidth required>
              <InputLabel>Company *</InputLabel>
              <MuiSelect
                value={formData.company_id}
                onChange={(e) => handleInputChange('company_id', e.target.value)}
                label="Company *"
                disabled={!editingProject && !!companyId}  // Disable when creating new project if company already selected
              >
                <MenuItem value="">
                  <em>Select Company</em>
                </MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.company_id} value={company.company_id}>
                    {company.company_code} - {company.company_name}
                  </MenuItem>
                ))}
              </MuiSelect>
            </FormControl>
            {!editingProject && companyId && (
              <Typography variant="caption" color="text.secondary">
                Company is auto-selected based on your current selection
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : editingProject ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={() => !deletingId && setDeleteConfirmDialog({ open: false, project: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 600 }}>
          Delete Project?
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. All data associated with this project will be permanently deleted.
          </Alert>
          {deleteConfirmDialog.project && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete this project?
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Project Number:</strong> {deleteConfirmDialog.project.project_number}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Project Name:</strong> {deleteConfirmDialog.project.project_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Company:</strong> {getCompanyName(deleteConfirmDialog.project.company_id)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setDeleteConfirmDialog({ open: false, project: null })}
            disabled={!!deletingId}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={!!deletingId}
            startIcon={deletingId ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {deletingId ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Projects;
